/**
 * SUBSCRIPTION UTILS - Funções Puras
 * 
 * Nenhuma dependência:
 * - Sem Firebase
 * - Sem Firestore
 * - Sem React
 * - Sem side effects
 * 
 * Funções puras 100% para cálculos de assinatura
 * 
 * @version 1.0.0
 */

import { Timestamp } from 'firebase/firestore';
import { DEFAULT_SUBSCRIPTION_DAYS } from '../constants/subscription';
import { SUBSCRIPTION_STATUS, SubscriptionStatusType } from '../constants/subscriptionStatus';

/**
 * Calcula a data de expiração da assinatura
 * 
 * @param startDate Data de início (Timestamp ou Date)
 * @param days Dias de duração (default: DEFAULT_SUBSCRIPTION_DAYS)
 * @returns Timestamp da data de expiração
 * 
 * @example
 * const expiresAt = calculateExpirationDate(
 *   Timestamp.now(),
 *   30
 * );
 */
export function calculateExpirationDate(
  startDate: Timestamp | Date,
  days: number = DEFAULT_SUBSCRIPTION_DAYS
): Timestamp {
  const date = startDate instanceof Timestamp
    ? startDate.toDate()
    : new Date(startDate);

  const expirationDate = new Date(date);
  expirationDate.setDate(expirationDate.getDate() + days);

  return Timestamp.fromDate(expirationDate);
}

/**
 * Calcula quantos dias faltam até expiração
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns Número de dias restantes (pode ser negativo se expirado)
 * 
 * @example
 * const daysRemaining = calculateDaysRemaining(
 *   Timestamp.fromDate(new Date('2026-07-14'))
 * ); // 30
 */
export function calculateDaysRemaining(
  expirationDate: Timestamp | Date | null | undefined
): number {
  if (!expirationDate) return 0;

  const now = new Date();
  const expDate = expirationDate instanceof Timestamp
    ? expirationDate.toDate()
    : new Date(expirationDate);

  const diffTime = expDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Verifica se a assinatura está expirada
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns true se expirada, false caso contrário
 * 
 * @example
 * const expired = isSubscriptionExpired(
 *   Timestamp.fromDate(new Date('2026-06-01'))
 * ); // true (data passada)
 */
export function isSubscriptionExpired(
  expirationDate: Timestamp | Date | null | undefined
): boolean {
  if (!expirationDate) return true;

  const now = new Date();
  const expDate = expirationDate instanceof Timestamp
    ? expirationDate.toDate()
    : new Date(expirationDate);

  return now > expDate;
}

/**
 * Formata data de expiração para exibição
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @param locale Locale para formatação (default: 'pt-BR')
 * @returns String formatada ou 'Sem assinatura' se nula
 * 
 * @example
 * const formatted = formatExpirationDate(
 *   Timestamp.fromDate(new Date('2026-07-14'))
 * ); // "14 de julho de 2026"
 */
export function formatExpirationDate(
  expirationDate: Timestamp | Date | null | undefined,
  locale: string = 'pt-BR'
): string {
  if (!expirationDate) return 'Sem assinatura';

  const date = expirationDate instanceof Timestamp
    ? expirationDate.toDate()
    : new Date(expirationDate);

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Formata ciclo de faturamento para exibição
 * 
 * @param cycle Ciclo de faturamento ('monthly' | 'annually' | etc)
 * @returns String formatada ou 'N/A' se inválido
 * 
 * @example
 * const formatted = formatBillingCycle('monthly');
 * // "Mensal"
 */
export function formatBillingCycle(cycle: string | null | undefined): string {
  if (!cycle) return 'N/A';

  const mapping: Record<string, string> = {
    'monthly': 'Mensal',
    'annually': 'Anual',
  };

  return mapping[cycle.toLowerCase()] || cycle;
}

/**
 * Formata plano de assinatura para exibição
 * 
 * @param plan Plano de assinatura ('manual' | 'automatic' | 'trial' | etc)
 * @returns String formatada ou 'N/A' se inválido
 * 
 * @example
 * const formatted = formatPlan('manual');
 * // "Manual"
 */
export function formatPlan(plan: string | null | undefined): string {
  if (!plan) return 'N/A';

  const mapping: Record<string, string> = {
    'manual': 'Manual',
    'automatic': 'Automático',
    'trial': 'Trial',
  };

  return mapping[plan.toLowerCase()] || plan;
}

// =====================================
// EXPIRATION ENGINE - Diagnóstico Puro
// =====================================

/**
 * Obtém o status diagnóstico da assinatura
 * 
 * FASE 3: Regras de Negócio
 * - VALID: dias > 7
 * - EXPIRING_SOON: 0 <= dias <= 7
 * - EXPIRED: dias < 0
 * - UNKNOWN: sem subscriptionExpiresAt
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns Status da assinatura (VALID | EXPIRING_SOON | EXPIRED | UNKNOWN)
 * 
 * @example
 * const status = getSubscriptionStatus(
 *   Timestamp.fromDate(new Date('2026-07-14'))
 * ); // 'valid' ou 'expiring_soon' etc
 */
export function getSubscriptionStatus(
  expirationDate: Timestamp | Date | null | undefined
): SubscriptionStatusType {
  if (!expirationDate) {
    return SUBSCRIPTION_STATUS.UNKNOWN;
  }

  const daysRemaining = calculateDaysRemaining(expirationDate);

  if (daysRemaining > 7) {
    return SUBSCRIPTION_STATUS.VALID;
  }

  if (daysRemaining >= 0 && daysRemaining <= 7) {
    return SUBSCRIPTION_STATUS.EXPIRING_SOON;
  }

  if (daysRemaining < 0) {
    return SUBSCRIPTION_STATUS.EXPIRED;
  }

  return SUBSCRIPTION_STATUS.UNKNOWN;
}

/**
 * Verifica se a assinatura está válida para uso
 * 
 * Válida = VALID ou EXPIRING_SOON
 * Inválida = EXPIRED ou UNKNOWN
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns true se válida (pode usar), false se expirada
 * 
 * @example
 * const isValid = isSubscriptionValid(subscription.expiresAt);
 */
export function isSubscriptionValid(
  expirationDate: Timestamp | Date | null | undefined
): boolean {
  const status = getSubscriptionStatus(expirationDate);
  return (
    status === SUBSCRIPTION_STATUS.VALID ||
    status === SUBSCRIPTION_STATUS.EXPIRING_SOON
  );
}

/**
 * Verifica se a assinatura está expirando em breve
 * 
 * EXPIRING_SOON = dias restantes entre 0 e 7
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns true se expirando em breve
 * 
 * @example
 * if (isExpiringSoon(subscription.expiresAt)) {
 *   showWarning("Renove sua assinatura em breve");
 * }
 */
export function isExpiringSoon(
  expirationDate: Timestamp | Date | null | undefined
): boolean {
  const status = getSubscriptionStatus(expirationDate);
  return status === SUBSCRIPTION_STATUS.EXPIRING_SOON;
}

/**
 * Diagnostica se o usuário deveria estar suspenso
 * 
 * APENAS DIAGNÓSTICO - Sem suspensão automática.
 * 
 * Deveria estar suspenso se:
 * - Assinatura expirada (status === EXPIRED)
 * 
 * Não suspende automaticamente.
 * Admin faz manualmente quando necessário.
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns true se assinatura expirada (candidato a suspensão manual)
 * 
 * @example
 * const needsSuspension = shouldBeSuspended(user.subscriptionExpiresAt);
 * if (needsSuspension) {
 *   console.warn("Usuário X deveria estar suspenso (assinatura expirada)");
 *   // Admin faz isso manualmente na interface
 * }
 */
export function shouldBeSuspended(
  expirationDate: Timestamp | Date | null | undefined
): boolean {
  const status = getSubscriptionStatus(expirationDate);
  return status === SUBSCRIPTION_STATUS.EXPIRED;
}

/**
 * Obtém dias restantes até expiração (alias para clareza)
 * 
 * Equivalente a calculateDaysRemaining()
 * Fornecido para melhor legibilidade em contextos diagnósticos.
 * 
 * @param expirationDate Data de expiração (Timestamp ou Date)
 * @returns Dias restantes (negativo se expirado, 0+ se válido)
 * 
 * @example
 * const days = getDaysRemaining(subscription.expiresAt);
 * console.log(`${days} dias até expiração`);
 */
export function getDaysRemaining(
  expirationDate: Timestamp | Date | null | undefined
): number {
  return calculateDaysRemaining(expirationDate);
}

/**
 * Formata status de assinatura para exibição
 * 
 * @param status Status da assinatura (VALID | EXPIRING_SOON | EXPIRED | UNKNOWN)
 * @returns String formatada em português
 * 
 * @example
 * const text = formatSubscriptionStatus(SUBSCRIPTION_STATUS.EXPIRING_SOON);
 * // "Expirando em breve"
 */
export function formatSubscriptionStatus(status: SubscriptionStatusType): string {
  const mapping: Record<SubscriptionStatusType, string> = {
    [SUBSCRIPTION_STATUS.VALID]: 'Válida',
    [SUBSCRIPTION_STATUS.EXPIRING_SOON]: 'Expirando em breve',
    [SUBSCRIPTION_STATUS.EXPIRED]: 'Expirada',
    [SUBSCRIPTION_STATUS.UNKNOWN]: 'Desconhecida',
  };

  return mapping[status] || 'Desconhecida';
}
